/**
 * /contact — where LandingPage11's two CTAs now go.
 *
 * LP11 used to send both buttons to /subscriptions. It now sends them here,
 * because the pitch on that page (a ready-made application foundation you point
 * AI at) is a conversation, not a self-serve checkout.
 *
 * The page is deliberately LP11's hero and nothing else: same navy gradient,
 * same blueprint grid, same orbs, same purple action button. Continuity is
 * the point — the click should not feel like leaving the page. It has its own
 * `contact-` prefixed stylesheet and its own token block, because LP11's
 * --lp11-* variables are scoped to `.lp11` and do not resolve here.
 */
import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "reactstrap";
import "./ContactPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClock, faComments, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useTranslation } from "react-i18next";
import useReveal from "hooks/useReveal";
import { requestSendContactMessage } from "../../services/userService";

const SUPPORT_EMAIL = "codenec4everyone@gmail.com";

/* Same shape the backend validates with. Deliberately loose: it rejects the
   obvious junk without bouncing the unusual-but-valid addresses a stricter
   pattern would. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const EMPTY = { name: "", email: "", business: "", message: "", website: "" };

/* Mirrors the caps in contactService.js, which now REJECTS over-length rather
   than truncating. Setting maxLength means a person using the form can never
   hit that rejection — the browser stops them at the boundary instead of the
   server bouncing a message they already wrote. */
const LIMITS = { name: 120, email: 254, business: 200, message: 5000 };

/* The backend's machine-readable codes mapped onto copy. Anything not listed
   here is an unexpected failure and gets the generic message. */
const BACKEND_ERRORS = {
    too_many_requests: "errorTooMany",
    too_long: "errorTooLong",
};

const ContactPage = () => {
    const { t, i18n } = useTranslation();

    const [form, setForm] = useState(EMPTY);
    const [isSending, setIsSending] = useState(false);
    const [sent, setSent] = useState(false);
    /* The KEY, not the translated sentence. Storing t(...) output would freeze
       the message in whatever language was active when it was set, and the
       navbar's language switcher does not remount this page — so an error
       raised in English stayed in English after switching to Spanish. */
    const [errorKey, setErrorKey] = useState("");
    const doneHeadingRef = useRef(null);

    /* Entrance for the copy column only. The form is never revealed: it is
       what the visitor came to type into, so it is there from the first paint. */
    useReveal();

    /* The transparent navbar, exactly as LP11 does it — but under this page's
       OWN body classes. Both must come off on unmount or every other route in
       the app inherits a transparent navbar. */
    useEffect(() => {
        document.body.classList.add("contact-nav-active");
        const onScroll = () => {
            document.body.classList.toggle("contact-nav-solid", window.scrollY > 40);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            document.body.classList.remove("contact-nav-active", "contact-nav-solid");
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    /* Arriving from LP11's FINAL cta means arriving from the bottom of a long
       page; without this the form opens scrolled past its own heading. */
    useEffect(() => {
        document.documentElement.scrollTop = 0;
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    }, []);

    /* Sending unmounts the whole form, including the button that had focus, so
       focus would otherwise fall back to <body> and a screen-reader user would
       hear NOTHING — no way to tell a sent message from a silent failure. The
       live region announces it; moving focus to the heading also puts them at
       the top of what replaced the form. */
    useEffect(() => {
        if (sent && doneHeadingRef.current) doneHeadingRef.current.focus();
    }, [sent]);

    const update = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
        if (errorKey) setErrorKey("");
    };

    /** @returns {string} a contactForm.* key, or "" when the form is good. */
    const firstProblem = () => {
        if (!form.name.trim()) return "errorRequiredName";
        if (!form.email.trim()) return "errorRequiredEmail";
        if (!EMAIL_SHAPE.test(form.email.trim())) return "errorInvalidEmail";
        if (!form.message.trim()) return "errorRequiredMessage";
        return "";
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (isSending) return;

        const problem = firstProblem();
        if (problem) {
            setErrorKey(problem);
            return;
        }

        setIsSending(true);
        setErrorKey("");
        try {
            await requestSendContactMessage({
                name: form.name.trim(),
                email: form.email.trim(),
                business: form.business.trim(),
                message: form.message.trim(),
                website: form.website,
                locale: i18n.language === "en" ? "en" : "es",
            });
            setSent(true);
            setForm(EMPTY);
        } catch (e) {
            /* The backend answers with machine-readable codes, not sentences —
               they are for the log, never for the person reading the form. */
            setErrorKey(BACKEND_ERRORS[e?.message] || "errorFailed");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <>
            <DemoNavbar />
            <main className="contact-page">
                <section className="contact-hero contact-grid">
                    <span className="cn-grain" aria-hidden="true" />
                    <span className="contact-orb contact-orb-1" aria-hidden="true" />
                    <span className="contact-orb contact-orb-2" aria-hidden="true" />
                    <span className="contact-orb contact-orb-3" aria-hidden="true" />

                    <Container>
                        <Row>
                            <Col lg="6" className="contact-copy">
                                <span className="contact-eyebrow" data-reveal>
                                    <span className="contact-dot" aria-hidden="true" />
                                    {t("contactForm.eyebrow")}
                                </span>
                                <h1
                                    className="contact-title"
                                    data-reveal
                                    style={{ "--reveal-delay": "60ms" }}
                                >
                                    {t("contactForm.title")}
                                </h1>
                                <p
                                    className="contact-lead"
                                    data-reveal
                                    style={{ "--reveal-delay": "120ms" }}
                                >
                                    {t("contactForm.lead")}
                                </p>

                                <div
                                    className="contact-aside"
                                    data-reveal
                                    style={{ "--reveal-delay": "180ms" }}
                                >
                                    <div className="contact-aside-line">
                                        <span className="contact-aside-icon" aria-hidden="true">
                                            <FontAwesomeIcon icon={faClock} />
                                        </span>
                                        <span>{t("contactForm.asideReply")}</span>
                                    </div>
                                    <div className="contact-aside-line">
                                        <span className="contact-aside-icon" aria-hidden="true">
                                            <FontAwesomeIcon icon={faComments} />
                                        </span>
                                        <span>{t("contactForm.asideBilingual")}</span>
                                    </div>
                                    <div className="contact-aside-line">
                                        <span className="contact-aside-icon" aria-hidden="true">
                                            <FontAwesomeIcon icon={faEnvelope} />
                                        </span>
                                        <span>
                                            {t("contactForm.asideEmail")}{" "}
                                            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                                        </span>
                                    </div>
                                </div>
                            </Col>

                            <Col lg="6" className="contact-card-wrap">
                                <div className="contact-card">
                                    {sent ? (
                                        <div className="contact-done" role="status" aria-live="polite">
                                            <span className="contact-done-icon" aria-hidden="true">
                                                <FontAwesomeIcon icon={faCheck} />
                                            </span>
                                            <h2 ref={doneHeadingRef} tabIndex={-1}>
                                                {t("contactForm.successTitle")}
                                            </h2>
                                            <p>{t("contactForm.successBody")}</p>
                                            <button
                                                type="button"
                                                className="contact-again"
                                                onClick={() => setSent(false)}
                                            >
                                                {t("contactForm.successAgain")}
                                            </button>
                                        </div>
                                    ) : (
                                        <form onSubmit={onSubmit} noValidate>
                                            <div className="contact-field">
                                                <label className="contact-label" htmlFor="contact-name">
                                                    {t("contactForm.nameLabel")}
                                                </label>
                                                <input
                                                    id="contact-name"
                                                    className="contact-input"
                                                    type="text"
                                                    autoComplete="name"
                                                    maxLength={LIMITS.name}
                                                    value={form.name}
                                                    onChange={update("name")}
                                                    placeholder={t("contactForm.namePlaceholder")}
                                                />
                                            </div>

                                            <div className="contact-field">
                                                <label className="contact-label" htmlFor="contact-email">
                                                    {t("contactForm.emailLabel")}
                                                </label>
                                                <input
                                                    id="contact-email"
                                                    className="contact-input"
                                                    type="email"
                                                    autoComplete="email"
                                                    maxLength={LIMITS.email}
                                                    value={form.email}
                                                    onChange={update("email")}
                                                    placeholder={t("contactForm.emailPlaceholder")}
                                                />
                                            </div>

                                            <div className="contact-field">
                                                <label className="contact-label" htmlFor="contact-business">
                                                    {t("contactForm.businessLabel")}
                                                </label>
                                                <input
                                                    id="contact-business"
                                                    className="contact-input"
                                                    type="text"
                                                    maxLength={LIMITS.business}
                                                    value={form.business}
                                                    onChange={update("business")}
                                                    placeholder={t("contactForm.businessPlaceholder")}
                                                />
                                            </div>

                                            <div className="contact-field contact-field-last">
                                                <label className="contact-label" htmlFor="contact-message">
                                                    {t("contactForm.messageLabel")}
                                                </label>
                                                <textarea
                                                    id="contact-message"
                                                    className="contact-textarea"
                                                    rows={5}
                                                    maxLength={LIMITS.message}
                                                    value={form.message}
                                                    onChange={update("message")}
                                                    placeholder={t("contactForm.messagePlaceholder")}
                                                />
                                            </div>

                                            {/* Honeypot. Off-screen, out of the tab order and hidden
                                                from screen readers, so only a form-filling bot ever
                                                puts anything in it. The backend drops those and still
                                                answers 200. */}
                                            <input
                                                className="contact-hp"
                                                type="text"
                                                name="website"
                                                tabIndex={-1}
                                                autoComplete="off"
                                                aria-hidden="true"
                                                value={form.website}
                                                onChange={update("website")}
                                            />

                                            {/* Above the button, not below it: the card is tall
                                                and the button sits at its foot, so an error
                                                underneath lands off-screen on a laptop and the
                                                form looks like it did nothing. */}
                                            {errorKey && (
                                                <div className="contact-error" role="alert">
                                                    {t(`contactForm.${errorKey}`)}
                                                </div>
                                            )}

                                            <button
                                                type="submit"
                                                className="contact-submit"
                                                disabled={isSending}
                                            >
                                                {isSending
                                                    ? t("contactForm.submitting")
                                                    : t("contactForm.submit")}
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </main>
            <MainFooter />
        </>
    );
};

export default ContactPage;
